import React from 'react'
import PropTypes from 'prop-types'
import SanityImage from 'gatsby-plugin-sanity-image-alt'
import { useSanityImageShape } from '../helpers/useSanityImageShape'
import {
  buildSanityImageSrcSet,
  buildSanityImageUrl,
} from '../helpers/image'

const SanityResponsiveImage = ({
  image,
  width,
  height,
  widths,
  quality,
  sizes,
  alt,
  priority = false,
  media,
  mobile,
  ...props
}) => {
  const imageShaped = useSanityImageShape(image)
  const src = buildSanityImageUrl(image, { width, height, quality })
  const srcSet = buildSanityImageSrcSet(image, {
    width,
    height,
    widths,
    quality,
  })
  const mobileSrcSet = mobile && buildSanityImageSrcSet(image, { ...mobile, quality })

  if (!imageShaped) return null

  const img = (
    <SanityImage
      {...imageShaped}
      {...props}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      loading={priority ? 'eager' : props.loading}
      // React 18 forwards this standard HTML attribute in lowercase.
      fetchpriority={priority ? 'high' : undefined}
      src={media ? 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7' : src}
      srcSet={media ? undefined : srcSet}
    />
  )

  // CSS-hidden eager images still download. A media-qualified source lets the
  // browser fetch only the matching desktop/mobile header before hydration.
  return media || mobile ? (
    <picture>
      {mobile && <source media="(max-width: 767.98px)" srcSet={mobileSrcSet} sizes={sizes} />}
      {media && <source media={media} srcSet={srcSet || src} sizes={sizes} />}
      {img}
    </picture>
  ) : img
}

SanityResponsiveImage.propTypes = {
  image: PropTypes.object,
  width: PropTypes.number.isRequired,
  height: PropTypes.number,
  widths: PropTypes.arrayOf(PropTypes.number),
  quality: PropTypes.number,
  sizes: PropTypes.string,
  alt: PropTypes.string.isRequired,
  priority: PropTypes.bool,
  media: PropTypes.string,
  mobile: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number,
    widths: PropTypes.arrayOf(PropTypes.number).isRequired,
  }),
}

SanityResponsiveImage.defaultProps = {
  image: null,
  height: null,
  widths: [320, 500, 768, 900, 1200],
  quality: 75,
  sizes: '100vw',
}

export default SanityResponsiveImage
